
import { generateCashFlow, calculateNPV, calculateIRR, calculatePaybackPeriod } from '../financial';
import type { CashFlowParams, ScenarioModeling } from './types';

/**
 * AI scenario modeling with Monte Carlo simulation
 */
export function aiScenarioModeling(
  assumptions: any,
  iterations: number = 1000
): ScenarioModeling {
  const results: Array<{ npv: number; irr: number; payback: number }> = [];
  
  // Monte Carlo simulation
  for (let i = 0; i < iterations; i++) {
    // Apply random variations to key assumptions
    const variationFactor = 1 + (Math.random() - 0.5) * 0.4; // ±20% variation
    
    const params: CashFlowParams = {
      initialInvestment: (assumptions.balanceSheet?.cashProjection || 100000) * variationFactor,
      annualRevenue: (assumptions.profitLoss?.revenueGrowthRate || 500000) * variationFactor,
      annualCosts: (assumptions.profitLoss?.costOfGoodsSoldPercentage || 325000) * variationFactor,
      growthRate: ((assumptions.profitLoss?.revenueGrowthRate || 10) / 100) * variationFactor,
      discountRate: (assumptions.general?.discountRate || 8) / 100,
      years: assumptions.general?.forecastPeriod || 5
    };
    
    const cashFlow = generateCashFlow(params);
    const npv = calculateNPV(cashFlow);
    const irr = calculateIRR(cashFlow);
    const payback = calculatePaybackPeriod(cashFlow);
    
    results.push({ npv, irr, payback });
  }
  
  // Sort results for percentile calculation
  results.sort((a, b) => a.npv - b.npv);
  
  const p10Index = Math.floor(iterations * 0.1);
  const p50Index = Math.floor(iterations * 0.5);
  const p90Index = Math.floor(iterations * 0.9);
  
  const scenarios = {
    pessimistic: results[p10Index],
    realistic: results[p50Index],
    optimistic: results[p90Index]
  };
  
  // Calculate probability distribution
  const positiveNPV = results.filter(r => r.npv > 0).length;
  const probabilityDistribution = [
    { outcome: 'Positive NPV', probability: positiveNPV / iterations },
    { outcome: 'Negative NPV', probability: (iterations - positiveNPV) / iterations }
  ];
  
  const expectedValue = results.reduce((sum, r) => sum + r.npv, 0) / iterations;
  
  return {
    scenarios,
    probabilityDistribution,
    expectedValue
  };
}

/**
 * Enhanced NPV calculation with sensitivity analysis
 */
export function aiEnhancedNPV(
  assumptions: any,
  sensitivityRanges: { [key: string]: number[] } = {}
): {
  baseNPV: number;
  sensitivityAnalysis: Array<{ variable: string; impact: number; range: number[] }>;
  recommendations: string[];
} {
  // Base case calculation
  const baseParams: CashFlowParams = {
    initialInvestment: assumptions.balanceSheet?.cashProjection || 100000,
    annualRevenue: assumptions.profitLoss?.revenueGrowthRate || 500000,
    annualCosts: assumptions.profitLoss?.costOfGoodsSoldPercentage || 325000,
    growthRate: (assumptions.profitLoss?.revenueGrowthRate || 10) / 100,
    discountRate: (assumptions.general?.discountRate || 8) / 100,
    years: assumptions.general?.forecastPeriod || 5
  };
  
  const baseCashFlow = generateCashFlow(baseParams);
  const baseNPV = calculateNPV(baseCashFlow);
  
  // Sensitivity analysis
  const sensitivityAnalysis = [];
  const variables = ['revenueGrowthRate', 'discountRate', 'costOfGoodsSoldPercentage'];
  
  variables.forEach(variable => {
    const range = sensitivityRanges[variable] || [-20, -10, 0, 10, 20]; // percentage changes
    const impacts = range.map(change => {
      const modifiedParams = { ...baseParams };
      
      switch (variable) {
        case 'revenueGrowthRate':
          modifiedParams.growthRate = ((assumptions.profitLoss?.revenueGrowthRate || 10) * (1 + change / 100)) / 100;
          break;
        case 'discountRate':
          modifiedParams.discountRate = ((assumptions.general?.discountRate || 8) * (1 + change / 100)) / 100;
          break;
        case 'costOfGoodsSoldPercentage':
          modifiedParams.annualCosts = (assumptions.profitLoss?.costOfGoodsSoldPercentage || 325000) * (1 + change / 100);
          break;
      }
      
      const modifiedCashFlow = generateCashFlow(modifiedParams);
      return calculateNPV(modifiedCashFlow);
    });
    
    const maxImpact = Math.max(...impacts) - baseNPV;
    const minImpact = Math.min(...impacts) - baseNPV;
    const impact = Math.max(Math.abs(maxImpact), Math.abs(minImpact));
    
    sensitivityAnalysis.push({ variable, impact, range });
  });
  
  // Generate recommendations based on sensitivity
  const recommendations: string[] = [];
  const mostSensitive = sensitivityAnalysis.reduce((max, current) => 
    current.impact > max.impact ? current : max
  );
  
  recommendations.push(`Focus on optimizing ${mostSensitive.variable} as it has the highest impact on NPV`);
  
  if (baseNPV < 0) {
    recommendations.push('Consider revising assumptions or exploring cost reduction strategies');
  }
  
  return {
    baseNPV,
    sensitivityAnalysis,
    recommendations
  };
}
